import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Target,
  Calendar,
  Search,
  AlertTriangle,
  Settings,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  FileText,
  Plus,
  TrendingUp,
  Shield,
  Bell,
  User,
  LogOut,
  MessageSquare,
  Bot,
  Zap,
  Package,
  IndianRupee,
  Trello,
  Upload,
  Download,
  History,
  Sliders,
  GitMerge,
  Wallet,
  BookOpen,
  Activity,
  CreditCard,
  Bug,
  ClipboardList,
  UserPlus,
  HeartPulse
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

export type NavigationItem = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badge?: string | number;
  children?: NavigationItem[];
  role?: string[];
};

interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard'
  },
  {
    id: 'daily-kpi',
    label: 'Daily KPIs',
    icon: Target,
    path: '/daily-kpi',
    role: ['CEO', 'GROUP_MANAGER', 'MANAGER', 'EXECUTIVE']
  },
  {
    id: 'pipeline-overview',
    label: 'Pipeline Overview',
    icon: BarChart3,
    path: '/pipeline-overview'
  },
  {
    id: 'pipeline',
    label: 'Pipeline Board',
    icon: Trello,
    path: '/pipeline',
    children: [
      {
        id: 'pipeline-board',
        label: 'View Pipeline',
        icon: Trello,
        path: '/pipeline'
      },
      {
        id: 'import-leads',
        label: 'Import Leads',
        icon: Upload,
        path: '/leads/import'
      }
    ]
  },
  {
    id: 'deliverables',
    label: 'Deliverables',
    icon: Package,
    path: '/deliverables',
    children: [
      {
        id: 'deliverables-list',
        label: 'All Deliverables',
        icon: Package,
        path: '/deliverables'
      },
      {
        id: 'deliverables-kanban',
        label: 'Kanban Board',
        icon: Trello,
        path: '/deliverables/kanban'
      },
      {
        id: 'deliverable-templates',
        label: 'Templates',
        icon: FileText,
        path: '/deliverable-templates'
      },
      {
        id: 'deliverables-analytics',
        label: 'Analytics',
        icon: BarChart3,
        path: '/deliverables/analytics'
      }
    ]
  },
  {
    id: 'tasks',
    label: 'Task Management',
    icon: Calendar,
    path: '/tasks'
  },
  {
    id: 'risk-assessment',
    label: 'Risk Assessment',
    icon: AlertTriangle,
    path: '/risk-assessment'
  },
  {
    id: 'duplicates',
    label: 'Duplicates',
    icon: GitMerge,
    path: '/duplicates',
    role: ['CEO', 'GROUP_MANAGER', 'MANAGER']
  },
  {
    id: 'whatsapp-management',
    label: 'WhatsApp Management',
    icon: MessageSquare,
    path: '/whatsapp-management',
    children: [
      {
        id: 'whatsapp-dashboard',
        label: 'WhatsApp Dashboard',
        icon: BarChart3,
        path: '/whatsapp-management/dashboard'
      },
      {
        id: 'whatsapp-sessions',
        label: 'Active Sessions',
        icon: Bot,
        path: '/whatsapp-management/sessions'
      },
      {
        id: 'whatsapp-triggers',
        label: 'Trigger System',
        icon: Zap,
        path: '/whatsapp-management/triggers'
      },
      {
        id: 'whatsapp-analytics',
        label: 'Analytics',
        icon: TrendingUp,
        path: '/whatsapp-management/analytics'
      }
    ]
  },
  {
    id: 'executive-performance',
    label: 'Executive Performance',
    icon: BarChart3,
    path: '/executive-performance',
    role: ['CEO']
  },
  {
    id: 'sales-dashboard',
    label: 'Sales Dashboard',
    icon: TrendingUp,
    path: '/sales-dashboard',
    role: ['CEO', 'GROUP_MANAGER', 'MANAGER', 'EXECUTIVE']
  },
  {
    id: 'user-management',
    label: 'User Management',
    icon: Users,
    path: '/user-management',
    role: ['CEO', 'GROUP_MANAGER']
  },
  {
    id: 'pending-approvals',
    label: 'Pending Approvals',
    icon: UserPlus,
    path: '/pending-approvals',
    role: ['CEO', 'GROUP_MANAGER']
  },
  {
    id: 'targets',
    label: 'Target Dashboard',
    icon: Target,
    path: '/targets',
    role: ['CEO', 'GROUP_MANAGER', 'MANAGER', 'EXECUTIVE']
  },
  {
    id: 'revenue-targets',
    label: 'Revenue Targets',
    icon: IndianRupee,
    path: '/revenue-targets',
    role: ['CEO', 'GROUP_MANAGER']
  },
  {
    id: 'finance',
    label: 'Finance Dashboard',
    icon: Wallet,
    path: '/finance',
    role: ['CEO', 'GROUP_MANAGER', 'MANAGER']
  },
  {
    id: 'invoices',
    label: 'Invoices',
    icon: FileText,
    path: '/invoices',
    role: ['CEO', 'GROUP_MANAGER', 'MANAGER']
  },
  {
    id: 'subscriptions',
    label: 'Subscriptions',
    icon: CreditCard,
    path: '/subscriptions',
    role: ['CEO', 'GROUP_MANAGER', 'MANAGER']
  },
  {
    id: 'client-health',
    label: 'Client Health',
    icon: HeartPulse,
    path: '/client-health',
    role: ['CEO', 'GROUP_MANAGER', 'MANAGER']
  },
  {
    id: 'stage-gate-config',
    label: 'Stage Gates',
    icon: Shield,
    path: '/stage-gate-config',
    role: ['CEO']
  },
  {
    id: 'audit-logs',
    label: 'Audit Logs',
    icon: History,
    path: '/audit-logs',
    role: ['CEO', 'MANAGER']
  },
  {
    id: 'activity-logs',
    label: 'Activity Logs',
    icon: Activity,
    path: '/activity-logs',
    role: ['CEO']
  },
  {
    id: 'report-issue',
    label: 'Report Issue',
    icon: Bug,
    path: '/report-issue'
  },
  {
    id: 'issue-management',
    label: 'Issue Management',
    icon: ClipboardList,
    path: '/issue-management',
    role: ['CEO', 'GROUP_MANAGER', 'MANAGER']
  },
  {
    id: 'documentation',
    label: 'Help & Docs',
    icon: BookOpen,
    path: '/documentation'
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    path: '/settings',
    children: [
      {
        id: 'custom-fields',
        label: 'Custom Fields',
        icon: Sliders,
        path: '/settings/custom-fields',
        role: ['CEO', 'GROUP_MANAGER']
      }
    ]
  }
];

export const Sidebar: React.FC<SidebarProps> = ({
  currentPage,
  onNavigate,
  isCollapsed,
  onToggleCollapse
}) => {
  const { user, logout } = useAuth();
  const [expandedItems, setExpandedItems] = useState<string[]>(['dashboard']);

  // Pending-approvals badge — only fetched for CEO/GROUP_MANAGER.
  // Poll every 60s per spec.
  const canApprove = !!user && ['CEO', 'GROUP_MANAGER'].includes(user.role);
  const { data: pendingUsers } = useQuery({
    queryKey: ['pending-users-count'],
    queryFn: api.getPendingUsers,
    enabled: canApprove,
    refetchInterval: 60000,
  });
  const pendingCount = Array.isArray(pendingUsers) ? pendingUsers.length : 0;

  // Apply dynamic badges by id
  const navigationItemsWithBadges: NavigationItem[] = navigationItems.map((item) => {
    if (item.id === 'pending-approvals' && pendingCount > 0) {
      return { ...item, badge: pendingCount };
    }
    return item;
  });

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleItemClick = (item: NavigationItem) => {
    if (item.children) {
      toggleExpanded(item.id);
    } else {
      onNavigate(item.path);
    }
  };

  const isItemVisible = (item: NavigationItem) => {
    if (!item.role) return true;
    return item.role.includes(user?.role || '');
  };

  const renderNavigationItem = (item: NavigationItem, level: number = 0) => {
    if (!isItemVisible(item)) return null;

    const isActive = currentPage === item.path;
    const isExpanded = expandedItems.includes(item.id);
    const hasChildren = item.children && item.children.length > 0;
    const visibleChildren = item.children?.filter(isItemVisible) || [];

    return (
      <div key={item.id}>
        <motion.button
          onClick={() => handleItemClick(item)}
          className={`
            w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
            ${isActive 
              ? 'bg-blue-100 text-blue-700 border border-blue-200' 
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
            }
            ${level > 0 ? 'ml-4' : ''}
          `}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center space-x-3">
            <item.icon className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
            {!isCollapsed && (
              <span className="truncate">{item.label}</span>
            )}
          </div>
          
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              {item.badge != null && item.badge !== '' && (
                <Badge
                  variant={item.id === 'pending-approvals' ? 'destructive' : 'secondary'}
                  className="text-xs px-1.5 py-0.5"
                >
                  {item.badge}
                </Badge>
              )}
              {hasChildren && (
                <motion.div
                  animate={{ rotate: isExpanded ? 90 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronRight className="h-3 w-3 text-gray-400" />
                </motion.div>
              )}
            </div>
          )}
        </motion.button>

        {/* Render children */}
        {hasChildren && !isCollapsed && (
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-1 space-y-1"
              >
                {visibleChildren.map(child => renderNavigationItem(child, level + 1))}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    );
  };

  return (
    <div className={`h-full bg-white border-r border-gray-200 flex flex-col ${isCollapsed ? 'w-16' : 'w-64'}`}>
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-900">WTF Amplify</h2>
                <p className="text-xs text-gray-500">CRM System</p>
              </div>
            </div>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
            className="h-8 w-8 p-0"
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        {navigationItemsWithBadges.map(item => renderNavigationItem(item))}
      </div>

      {/* User Section */}
      {!isCollapsed && user && (
        <div className="flex-shrink-0 p-3 border-t border-gray-200">
          <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user.role || 'Unknown'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
            >
              <LogOut className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}; 